import { useEffect, useState } from 'react'
import { CashFund, Expense, PaymentOrder, PaymentOrderStatus, Transaction, useCashFundLoaders, useExpenseLoaders, usePaymentOrderLoaders, useTransactionLoaders } from '../../../administration'
import { useSystemStateHooks } from '../../../system'

export type ManagerFinanceData = {
    cashFunds: CashFund[]
    expenses: Expense[]
    pendingPaymentOrders: PaymentOrder[]
    // Movements of the current month (to get the cash funds balance of the day and of the month)
    monthTransactions: Transaction[]
}

// enabled: false when no visible card needs this data (it isn't loaded)
export const useManagerFinanceData = (enabled = true) => {
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState(false)
    const [data, setData] = useState<ManagerFinanceData | null>(null)

    const { companyId } = useSystemStateHooks()
    const cashFundLoaders = useCashFundLoaders()
    const expenseLoaders = useExpenseLoaders()
    const paymentOrderLoaders = usePaymentOrderLoaders()
    const transactionLoaders = useTransactionLoaders()

    useEffect(() => {
        if (!enabled) return
        setLoading(true)
        const load = async () => {
            try {
                const now = new Date()
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
                const [allCashFunds, allExpenses, allPaymentOrders, transactions] = await Promise.all([
                    cashFundLoaders(companyId).loadAllCashFunds(),
                    expenseLoaders(companyId).loadAllExpenses(),
                    paymentOrderLoaders(companyId).loadAllPaymentOrders(),
                    transactionLoaders(companyId).loadTransactions(monthStart, now)
                ])
                setData({
                    cashFunds: (allCashFunds as CashFund[]).filter(cashFund => !cashFund.archived),
                    expenses: (allExpenses as Expense[]).filter(expense => !expense.archived),
                    pendingPaymentOrders: (allPaymentOrders as PaymentOrder[]).filter(
                        paymentOrder => !paymentOrder.archived && paymentOrder.status === PaymentOrderStatus.EMITTED
                    ),
                    monthTransactions: transactions
                })
            } catch {
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [enabled])

    return { loading, error, data }
}
